import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ListFilter, Search } from 'lucide-react';
import { useState } from 'react';

import { useSiteScope } from '@/app/site/useSiteScope';
import type { FieldBody, FieldFilter } from '@/shared/api/generated/model';
import { recordsList } from '@/shared/api/generated/records/records';
import { cx } from '@/shared/lib/cx';
import { pluralize } from '@/shared/lib/plural';
import { useDebounced } from '@/shared/lib/useDebounced';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Sheet, SheetContent, SheetTrigger } from '@/shared/ui/Sheet';

import {
  PANEL_ORDER,
  activeFields,
  chosenFilter,
  chosenOf,
  filtersOf,
  panelKind,
  valueOf,
  withField,
  type PanelKind,
} from './listModel';
import styles from './FiltersPanel.module.scss';

/**
 * Фильтры записей — строятся из полей модели сами: варианты и ссылки —
 * флажками («любое из»), числа и даты — «от и до», да/нет, текст —
 * «содержит», файлы и группы — заполнено или пусто. На широком экране —
 * колонкой справа от списка (FiltersBody), на узком — шторкой по кнопке
 * (FiltersPanel). Условия — те же FieldFilter, что в сохранённых видах.
 */
export function FiltersBody({
  fields,
  filters,
  onChange,
}: {
  fields: FieldBody[];
  filters: FieldFilter[];
  onChange: (filters: FieldFilter[]) => void;
}) {
  const shown = fields
    .flatMap((field) => {
      const kind = panelKind(field.type);

      return kind === null ? [] : [{ field, kind }];
    })
    .sort((a, b) => PANEL_ORDER.indexOf(a.kind) - PANEL_ORDER.indexOf(b.kind));

  if (shown.length === 0) {
    return <p className={styles['muted']}>У полей этой модели фильтров нет.</p>;
  }

  return (
    <div className={styles['body']}>
      {shown.map(({ field, kind }) => (
        <Section key={field.key} field={field} kind={kind} filters={filters} onChange={onChange} />
      ))}
    </div>
  );
}

/** Колонка фильтров справа от списка — на широком экране всегда на виду. */
export function FiltersSide({
  fields,
  filters,
  onChange,
}: {
  fields: FieldBody[];
  filters: FieldFilter[];
  onChange: (filters: FieldFilter[]) => void;
}) {
  return (
    <aside className={styles['side']} aria-label="Фильтры">
      <div className={styles['side__head']}>
        <h2 className={styles['side__title']}>
          <ListFilter size={16} />
          Фильтры
        </h2>
        {filters.length > 0 && (
          <button
            type="button"
            className={styles['side__reset']}
            onClick={() => {
              onChange([]);
            }}
          >
            Сбросить · {activeFields(filters)}
          </button>
        )}
      </div>
      <FiltersBody fields={fields} filters={filters} onChange={onChange} />
    </aside>
  );
}

/** Шторка с фильтрами — для узкого экрана, где колонке справа нет места. */
export function FiltersPanel({
  fields,
  filters,
  total,
  className,
  onChange,
}: {
  fields: FieldBody[];
  filters: FieldFilter[];
  /** Сколько записей подходит сейчас — на кнопке «Показать». */
  total: number | undefined;
  className?: string | undefined;
  onChange: (filters: FieldFilter[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = activeFields(filters);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          className={className}
          variant={active > 0 ? 'solid' : 'outline'}
          tone="neutral"
          iconLeft={<ListFilter size={15} />}
        >
          Фильтры{active > 0 ? ` · ${String(active)}` : ''}
        </Button>
      </SheetTrigger>

      <SheetContent
        title="Фильтры"
        footer={
          <>
            {filters.length > 0 && (
              <Button
                variant="ghost"
                tone="neutral"
                onClick={() => {
                  onChange([]);
                }}
              >
                Сбросить
              </Button>
            )}
            <Button
              onClick={() => {
                setOpen(false);
              }}
            >
              {total === undefined
                ? 'Показать'
                : `Показать ${pluralize(total, ['запись', 'записи', 'записей'])}`}
            </Button>
          </>
        }
      >
        <FiltersBody fields={fields} filters={filters} onChange={onChange} />
      </SheetContent>
    </Sheet>
  );
}

/** Значение условия в поле ввода: строка или число, иначе — пусто. */
function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

/** Раздел поля: раскрыт сразу, заголовок сворачивает его. */
function Section({
  field,
  kind,
  filters,
  onChange,
}: {
  field: FieldBody;
  kind: PanelKind;
  filters: FieldFilter[];
  onChange: (filters: FieldFilter[]) => void;
}) {
  const mine = filtersOf(filters, field.key);
  const [open, setOpen] = useState(true);
  const set = (next: FieldFilter[]) => {
    onChange(withField(filters, field.key, next));
  };

  return (
    <section className={styles['section']}>
      <button
        type="button"
        className={styles['section__head']}
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        <span>
          {field.name}
          {mine.length > 0 && <span className={styles['section__dot']} aria-label="есть условие" />}
        </span>
        <ChevronDown
          size={16}
          className={cx(styles['section__chevron'], open && styles['section__chevron--open'])}
        />
      </button>

      {open && (
        <div className={styles['section__body']}>
          {kind === 'choices' && <Choices field={field} filters={filters} set={set} />}
          {kind === 'reference' && <References field={field} filters={filters} set={set} />}
          {kind === 'boolean' && (
            <Segmented
              value={
                mine.length === 0
                  ? 'any'
                  : valueOf(filters, field.key, 'eq') === true
                    ? 'yes'
                    : 'no'
              }
              aria-label={field.name}
              onValueChange={(next) => {
                set(next === 'any' ? [] : [{ field: field.key, op: 'eq', value: next === 'yes' }]);
              }}
              options={[
                { value: 'any', label: 'Все' },
                { value: 'yes', label: 'Да' },
                { value: 'no', label: 'Нет' },
              ]}
            />
          )}
          {(kind === 'range' || kind === 'dates') && (
            <Range field={field} kind={kind} filters={filters} set={set} />
          )}
          {kind === 'text' && (
            <Input
              placeholder="Содержит…"
              aria-label={`${field.name}: содержит`}
              clearable
              value={text(valueOf(filters, field.key, 'contains'))}
              onChange={(event) => {
                const value = event.target.value;
                set(value === '' ? [] : [{ field: field.key, op: 'contains', value }]);
              }}
            />
          )}
          {kind === 'presence' && (
            <Segmented
              value={
                mine[0]?.op === 'filled' ? 'filled' : mine[0]?.op === 'empty' ? 'empty' : 'any'
              }
              aria-label={field.name}
              onValueChange={(next) => {
                set(next === 'any' ? [] : [{ field: field.key, op: next as 'filled' | 'empty' }]);
              }}
              options={[
                { value: 'any', label: 'Все' },
                { value: 'filled', label: 'Заполнено' },
                { value: 'empty', label: 'Пусто' },
              ]}
            />
          )}
        </div>
      )}
    </section>
  );
}

/** Варианты списка — флажками, отмечено несколько — «любое из». */
function Choices({
  field,
  filters,
  set,
}: {
  field: FieldBody;
  filters: FieldFilter[];
  set: (next: FieldFilter[]) => void;
}) {
  const raw = (field.options as Record<string, unknown>)['choices'];
  const choices = Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === 'string')
    : [];
  const chosen = chosenOf(filters, field.key);
  const [all, setAll] = useState(false);

  if (choices.length === 0) {
    return <p className={styles['muted']}>У поля нет вариантов.</p>;
  }

  // Отмеченные видны всегда, даже если они за первыми восемью.
  const visible = all
    ? choices
    : choices.filter((choice, i) => i < MANY || chosen.includes(choice));

  return (
    <div className={styles['checks']}>
      {visible.map((choice) => (
        <Checkbox
          key={choice}
          checked={chosen.includes(choice)}
          onCheckedChange={(on) => {
            set(
              chosenFilter(
                field.key,
                on === true ? [...chosen, choice] : chosen.filter((item) => item !== choice),
              ),
            );
          }}
        >
          {choice}
        </Checkbox>
      ))}
      {choices.length > MANY && (
        <button
          type="button"
          className={styles['more']}
          onClick={() => {
            setAll((value) => !value);
          }}
        >
          {all ? 'Свернуть' : `Ещё ${String(choices.length - visible.length)}`}
        </button>
      )}
    </div>
  );
}

/** Сколько вариантов видно, пока список не развёрнут. */
const MANY = 8;

/** Ссылка — флажками записей той коллекции, с поиском по названию. */
function References({
  field,
  filters,
  set,
}: {
  field: FieldBody;
  filters: FieldFilter[];
  set: (next: FieldFilter[]) => void;
}) {
  const scope = useSiteScope();
  const collection = text((field.options as Record<string, unknown>)['collection']);
  const target = scope?.types.find((type) => type.slug === collection);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search);
  const chosen = chosenOf(filters, field.key);

  const records = useQuery({
    queryKey: ['records', target?.id, { search: debounced, filter: true }],
    enabled: target !== undefined,
    queryFn: async () => {
      const response = await recordsList(target?.id ?? '', { search: debounced, per_page: 50 });

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос записей');
      }

      return response.data.items ?? [];
    },
  });

  if (target === undefined) {
    return <p className={styles['muted']}>Ссылка на любую коллекцию — фильтровать не по чему.</p>;
  }

  return (
    <div className={styles['refs']}>
      <Input
        placeholder={`Найти в «${target.name}»`}
        aria-label={`Поиск в «${target.name}»`}
        prefix={<Search size={14} />}
        clearable
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />
      {records.isLoading && <p className={styles['muted']}>Загружаем…</p>}
      {records.data?.length === 0 && <p className={styles['muted']}>Ничего не нашлось.</p>}
      <div className={styles['checks']}>
        {records.data?.map((record) => (
          <Checkbox
            key={record.id}
            checked={chosen.includes(record.id)}
            onCheckedChange={(on) => {
              set(
                chosenFilter(
                  field.key,
                  on === true
                    ? [...chosen, record.id]
                    : chosen.filter((item) => item !== record.id),
                ),
              );
            }}
          >
            {record.title || 'Без названия'}
          </Checkbox>
        ))}
      </div>
      {chosen.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          tone="neutral"
          onClick={() => {
            set([]);
          }}
        >
          Снять выбор ({chosen.length})
        </Button>
      )}
    </div>
  );
}

/** Число или дата — «от и до»: два условия, «не меньше» и «не больше». */
function Range({
  field,
  kind,
  filters,
  set,
}: {
  field: FieldBody;
  kind: 'range' | 'dates';
  filters: FieldFilter[];
  set: (next: FieldFilter[]) => void;
}) {
  const from = valueOf(filters, field.key, 'gte');
  const to = valueOf(filters, field.key, 'lte');
  const type = kind === 'range' ? 'number' : 'date';
  const parse = (raw: string) => (raw === '' ? undefined : kind === 'range' ? Number(raw) : raw);

  const update = (gte: unknown, lte: unknown) => {
    set([
      ...(gte === undefined ? [] : [{ field: field.key, op: 'gte' as const, value: gte }]),
      ...(lte === undefined ? [] : [{ field: field.key, op: 'lte' as const, value: lte }]),
    ]);
  };

  return (
    <div className={styles['range']}>
      <Input
        type={type}
        placeholder="от"
        aria-label={`${field.name}: от`}
        value={text(from)}
        onChange={(event) => {
          update(parse(event.target.value), to);
        }}
      />
      <span className={styles['muted']}>—</span>
      <Input
        type={type}
        placeholder="до"
        aria-label={`${field.name}: до`}
        value={text(to)}
        onChange={(event) => {
          update(from, parse(event.target.value));
        }}
      />
    </div>
  );
}
